import { LitElement, html, nothing, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { MultiLevelListEvents, MultiLevelListItem } from '../types';
import { classMap } from 'lit/directives/class-map.js';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { InputWidthValues } from '../../gzInput/types';
import { GzInputSearchWebComponent } from '../../gzInput';
import { emphasizeString } from 'apps/webcomponents/src/utils/htmlElement.utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 * @summary This component displays nested unordered lists
 * @tag gz-multi-level-list
 */
@customElement('gz-multi-level-list')
export class GzMultiLevelListWebComponent extends LitElement {
  @property({ type: Array, attribute: true, reflect: true })
  listItems: MultiLevelListItem[] = [];

  @property({ type: Boolean, attribute: true, reflect: true })
  hasSearch = false;

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholder = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  @query('gz-input-search')
  private readonly searchInput!: GzInputSearchWebComponent;

  @state()
  private searchString: string = '';

  @state() private filteredListItems: MultiLevelListItem[] = [];

  private readonly listItemHeight = 32;
  private readonly searchMinNrOfEntries = 6;
  private bodyRect: DOMRect;
  private windowHeight: number;
  private windowWidth: number;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.memoizeWindowConstants();
    this.filteredListItems = [...this.listItems];
    this.focusSearchInput();
    window.addEventListener('resize', this.memoizeWindowConstants.bind(this));
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.memoizeWindowConstants.bind(this));
    this.clearSearch();
  }

  updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('listItems') && this.listItems) {
      this.filteredListItems = [...this.listItems];

      // Clear any existing search
      if (this.searchInput?.value) {
        this.searchString = '';
        this.searchInput.setValue('');
      }
    }
  }

  private renderListItem(item: MultiLevelListItem): TemplateResult {
    const classes = {
      'list-item': true,
      'has-sub-list': !!item.children && item.children.length > 0,
      [item.textColor || '']: !!item.textColor,
      'has-divider': this.shouldShowDivider(item),
    };

    const name = this.searchString ? this.decoratedTextOnSearch(this.searchString, item.name) : item.name;

    return html`<li
      ?disabled="${item.disabled}"
      class=${classMap(classes)}
      role="menuitem"
      tabindex="0"
      aria-label="${item.name}"
      @mouseover="${e => this.onListItemMouseEnter(item, e)}"
      @mouseleave="${e => this.onListItemMouseLeave(item, e)}"
      @click="${() => this.onListItemClick(item)}"
    >
      <gz-tooltip isEllipsis tooltipText="${item.name}" class="tooltip">
        <span>${name}</span>
      </gz-tooltip>
      ${item.children && item.children.length > 0
        ? html`<gz-icon name="ds-icon-calendar_next" size="24" color="secondary" class="list-icon"></gz-icon>`
        : nothing}
      ${item.children
        ? html`
            <div class="sub-list-wrapper">
              <ul role="menu" class="list-unstyled sub-list" aria-hidden="true">
                ${item.children.map((child: MultiLevelListItem) => this.renderListItem(child))}
              </ul>
            </div>
          `
        : nothing}
    </li>`;
  }

  private shouldShowDivider(item: MultiLevelListItem): boolean {
    return !!item.hasDivider && !(this.hasSearch && this.searchString);
  }

  render(): TemplateResult {
    const classes = {
      'has-search': this.hasSearch && this.shouldShowSearch(),
      list: true,
      'list-unstyled': true,
    };

    return html`<ul class=${classMap(classes)} role="menu">
      ${this.renderSearchInput()}${this.hasSearch ? this.renderNoResultsTemplate() : nothing}
      ${this.filteredListItems
        ? this.filteredListItems.map((item: MultiLevelListItem) => this.renderListItem(item))
        : nothing}
    </ul> `;
  }

  public clearSearch(): void {
    if (!this.hasSearch) {
      return;
    }

    this.searchString = '';
    this.filteredListItems = [...this.listItems];
    this.updateComplete.then(() => {
      if (this.searchInput) {
        this.searchInput.setValue('');
      }
    });
  }

  protected renderNoResultsTemplate(): TemplateResult {
    const classes = {
      'no-results': true,
      visible: this.filteredListItems.length === 0 && this.searchString?.length > 0,
    };
    return html`<div class="${classMap(classes)}">${this.noSearchResultsString}</div>`;
  }

  private renderSearchInput(): TemplateResult {
    if (!this.hasSearch || !this.shouldShowSearch()) {
      return html``;
    }

    return html`<div class="search-wrapper">
      <gz-input-search
        inputwidth="${InputWidthValues.FULL}"
        placeholder="${this.searchPlaceholder}"
        gz-form-input
        @change="${(e: CustomEvent) => this.handleSearch(e)}"
      ></gz-input-search>
    </div>`;
  }

  private shouldShowSearch(): boolean {
    if (this.searchString) {
      return true;
    }

    if (this.hasSearch && this.searchInput?.value) {
      return true;
    }

    // Check if any top-level list item has children
    if (this.listItems?.some(item => item.children && item.children.length > 0)) {
      return true;
    }

    return false;
  }

  private handleSearch(event: CustomEvent): void {
    const searchString = event.detail.sourceEvent.toLowerCase();
    this.searchString = searchString;
    const addedNames = new Set<string>();

    if (!searchString) {
      this.filteredListItems = [...this.listItems];
      return;
    }

    const flattenAndFilterItems = (items: MultiLevelListItem[]): MultiLevelListItem[] => {
      const result: MultiLevelListItem[] = [];

      items.forEach(item => {
        const matches = item.name.toLowerCase().includes(searchString);
        const hasChildren = item.children && item.children.length > 0;

        if (!hasChildren) {
          // avoid duplicates by checking if the name has already been added
          if (matches && !addedNames.has(item.name)) {
            result.push({ ...item, children: [] });
            addedNames.add(item.name);
          }
        } else {
          const filteredChildren = flattenAndFilterItems(item.children || []);

          result.push(...filteredChildren);
        }
      });

      return result;
    };

    this.filteredListItems = flattenAndFilterItems(this.listItems);
  }

  private decoratedTextOnSearch(searchString: string, text: string) {
    return unsafeHTML(`${emphasizeString(searchString, text)}`);
  }

  private memoizeWindowConstants(): void {
    this.bodyRect = document.body.getBoundingClientRect();
    this.windowHeight = window.innerHeight;
    this.windowWidth = window.innerWidth;
  }

  public focusSearchInput(): void {
    if (this.hasSearch) {
      this.updateComplete.then(() => {
        this.searchInput?.focusInput();
      });
    }
  }

  private getElementFromEvent(e: MouseEvent, tagName: string): EventTarget | undefined {
    return Array.from(e.composedPath()).find((el: EventTarget) => el instanceof Element && el.localName === tagName);
  }

  private positionSubList(listItem: HTMLElement, subList: HTMLElement): void {
    const liRect: DOMRect = listItem.getBoundingClientRect();

    const topOffset: number = liRect.top - this.bodyRect.top;
    const leftOffset: number = liRect.left - this.bodyRect.left + liRect.width;

    let topPosition: number = topOffset;
    let leftPosition: number = leftOffset;

    // Check if sub-list will overflow below the viewport
    if (topOffset + subList.offsetHeight > this.windowHeight) {
      // Calculate the position to align to the bottom of the last list item
      topPosition = topOffset + this.listItemHeight - subList.offsetHeight;
    }

    // Check if sub-list will overflow to the right of the viewport
    if (leftOffset + subList.offsetWidth > this.windowWidth) {
      // Position the sublist to the left side of the list item
      leftPosition = liRect.left - this.bodyRect.left - subList.offsetWidth;
    }

    subList.style.top = topPosition + 'px';
    subList.style.left = leftPosition + 'px';
  }

  private onListItemMouseLeave(item: MultiLevelListItem, event: MouseEvent): void {
    if (isEmpty(item.children)) {
      return;
    }

    const listItem = this.getElementFromEvent(event, 'li') as HTMLElement;
    const subList = listItem.querySelector('.sub-list-wrapper') as HTMLElement;
    if (listItem && subList) {
      subList.style.display = 'none';
    }
  }

  private onListItemMouseEnter(item: MultiLevelListItem, event: MouseEvent): void {
    if (isEmpty(item.children)) {
      return;
    }
    const listItem = this.getElementFromEvent(event, 'li') as HTMLElement;
    const subList = listItem.querySelector('.sub-list-wrapper') as HTMLElement;

    if (listItem && subList) {
      subList.style.display = 'block';
      this.positionSubList(listItem, subList);
    }
  }

  private onListItemClick(item: MultiLevelListItem): void {
    if (item.children?.length || item.disabled) {
      return;
    }

    if (item.action) {
      item.action();
    }

    this.dispatchEvent(new CustomEvent(MultiLevelListEvents.ITEM_CLICKED, { detail: item }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-multi-level-list': GzMultiLevelListWebComponent;
  }
}
